import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useCandidateDetailsQueryParams } from '@percent/admin-dashboard/app/grants/programs/grantsProgramDetails/hooks/useCandidateDetailsQueryParams'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Flex, Loader, SidePanel, Tab } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import { ProgramCandidates } from './programCandidates/ProgramCandidates'
import { ProgramHeader } from './programHeader/ProgramHeader'
import { ProgramTab } from './programHeader/ProgramHeader.types'
import { ProgramOverview } from './programOverview/ProgramOverview'
import { ProgramStateMachine } from './programStateMachine/ProgramStateMachine'
import { PreviewForm } from './programTaskTemplates/form/previewForm/PreviewForm'
import { ProgramTaskTemplates } from './programTaskTemplates/ProgramTaskTemplates'

export function GrantsProgramDetailsContainer() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const [activeTab, setActiveTab] = useState<ProgramTab>(ProgramTab.OVERVIEW)
  const { queryParams, resetQueryParams } = useCandidateDetailsQueryParams()

  const { grantProgramService } = useServices()

  const {
    data: program,
    isLoading,
    error,
    refetch
  } = useQuery(['/v1/admin/grants/programs', id], grantProgramService.getProgramById, {
    programId: id || ''
  })

  const {
    data: stateMachineMetadata,
    isLoading: isMetadataLoading,
    error: metadataError
  } = useQuery(['state-machine-metadata', id], grantProgramService.getStateMachineMetadata, {
    programId: id || ''
  })

  if (isLoading || isMetadataLoading) return <Loader />

  if (!program || error || metadataError) return <ErrorView errorMessage={t('grants.programDetails.error.title')} />

  return (
    <Flex direction="row" gap={16} align="stretch">
      <Flex gap={16} direction="column" flex={1}>
        <ProgramHeader activeTab={activeTab} setActiveTab={setActiveTab} program={program} onProgramAction={refetch} />
        <Tab value={activeTab} index={ProgramTab.OVERVIEW}>
          <ProgramOverview program={program} />
        </Tab>
        <Tab value={activeTab} index={ProgramTab.STATE_MACHINE}>
          <ProgramStateMachine
            stateMachineDefinition={program.stateMachineDefinition}
            metadata={stateMachineMetadata!}
          />
        </Tab>
        <Tab value={activeTab} index={ProgramTab.CANDIDATES}>
          <ProgramCandidates />
        </Tab>
        <Tab value={activeTab} index={ProgramTab.TASK_TEMPLATES}>
          <ProgramTaskTemplates />
        </Tab>
      </Flex>

      {queryParams.previewTask && (
        <SidePanel title={queryParams.taskTemplateName || ''} onClose={resetQueryParams} style={{ minWidth: 480 }}>
          <PreviewForm formId={queryParams.previewTask} />
        </SidePanel>
      )}
    </Flex>
  )
}
